import { useModal } from "@/Contexts";
import { PlusIcon } from "@/Icons/v2/Plus";
import { useTerminalsState } from "@/States/Terminals";
import { useGlobalAlert } from "@/States/globalAlert";
import { trpc } from "@/Utils/trpc";
import { ViewWithLoader } from "@/components/v2/ViewWithLoader";
import { CLIPPED_BUTTON_STYLE } from "@/constants/general";
import { useTrans } from "@/translations";

import { useCallback, useMemo, useState } from "react";

import { twMerge } from "tailwind-merge";

import { FileDisplay } from "./components/fileDisplay";
import { SearchBar } from "./components/searchBar";
import { UploadFileModal } from "./components/uploadFileModal";
import { LibraryPageContext } from "./context";
import {
  FileSortKey,
  FileStatus,
  type LibraryFile,
  SortDirection,
} from "./types";
import { getDisplayedFiles } from "./utils";

export const FilesView: React.FC = () => {
  const { t } = useTrans();
  const { showModal } = useModal();
  const { setGlobalAlert } = useGlobalAlert();
  const { currentTerminal } = useTerminalsState();

  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedViewId, setSelectedViewId] = useState<FileStatus | null>(
    FileStatus.PUBLISHED,
  );
  const [selectedFolderName, setSelectedFolderName] = useState<string>("");
  const [sortKey, setSortKey] = useState<FileSortKey>(FileSortKey.DATE);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.DESC,
  );

  const {
    data: folders,
    refetch: refetchFolders,
    isLoading: isInitialLoadingFolders,
    isFetching: isFetchingFolders,
  } = trpc.library.getFolders.useQuery({
    terminalId: currentTerminal?.id!,
  });

  const {
    data: allFilesData,
    refetch: refetchFiles,
    isLoading: isInitialLoadingFiles,
    isFetching: isFetchingFiles,
  } = trpc.library.getAllFiles.useQuery({
    terminalId: currentTerminal?.id!,
  });

  const { mutate: createFolder } = trpc.library.createFolder.useMutation({
    onSuccess: () => {
      refetchFolders();
    },
    onError: (error) => {
      setGlobalAlert({
        type: "error",
        message: "errors:documentDeleteFailed",
      });
      console.error("Error deleting document:", error);
    },
  });

  const allFiles = useMemo<LibraryFile[]>(
    () =>
      (allFilesData ?? []).map((file) => ({
        ...file,
        status: file.status as string as FileStatus,
        folderName: file.folderName ?? "",
        date: file.date ?? new Date(0),
      })),
    [allFilesData],
  );

  const displayedFiles = useMemo(
    () =>
      getDisplayedFiles({
        allFiles,
        selectedFolderName,
        selectedViewId,
        searchValue,
        sortKey,
        sortDirection,
      }),
    [
      allFiles,
      selectedFolderName,
      selectedViewId,
      searchValue,
      sortKey,
      sortDirection,
    ],
  );

  const contextValue = useMemo(
    () => ({
      folders: folders || [],
      allFiles,
      displayedFiles,
      refetchFolders,
      refetchFiles,
      selectedViewId,
      setSelectedViewId,
      selectedFolderName,
      setSelectedFolderName,
      searchValue,
      setSearchValue,
      sortKey,
      setSortKey,
      sortDirection,
      setSortDirection,
      createFolder,
      isFetchingFolders,
      isFetchingFiles,
    }),
    [
      folders,
      allFiles,
      displayedFiles,
      refetchFolders,
      selectedViewId,
      selectedFolderName,
      searchValue,
      sortKey,
      sortDirection,
      createFolder,
      isFetchingFolders,
      refetchFiles,
      isFetchingFiles,
    ],
  );

  const showLoading = useMemo(
    () => isInitialLoadingFolders || isInitialLoadingFiles,
    [isInitialLoadingFolders, isInitialLoadingFiles],
  );

  const openUploadFileModal = useCallback(() => {
    showModal(
      <LibraryPageContext value={contextValue}>
        <UploadFileModal />
      </LibraryPageContext>,
    );
  }, [showModal, contextValue]);

  return (
    <LibraryPageContext value={contextValue}>
      <ViewWithLoader isLoading={showLoading}>
        <div className="mt-[22px]">
          <div className="flex flex-wrap items-center gap-[12px]">
            <SearchBar />
            <div className="flex-1" />
            <button
              type="button"
              className={twMerge(
                "inline-flex cursor-pointer items-center gap-[9px] bg-black px-5 py-[10px] text-[13.5px] font-bold text-white hover:bg-[#3a3838]",
                CLIPPED_BUTTON_STYLE,
              )}
              onClick={openUploadFileModal}
            >
              <PlusIcon />
              {t("libraryPage:uploadFileButton")}
            </button>
          </div>

          <FileDisplay />
        </div>
      </ViewWithLoader>
    </LibraryPageContext>
  );
};
